import { gsap } from 'gsap';
import { Container, Sprite, type Texture } from 'pixi.js';
import { BATTLE_PACE_SCALE } from '../data/constants';
import type { UiTextures } from '../render/assets';
import type { DesignViewport } from '../render/layout';
import { CityTab } from './CityTab';
import type { EnemyCardData } from './EnemyCard';
import { FightsTab } from './FightsTab';
import { GameWindow } from './GameWindow';
import { SkillsTab } from './SkillsTab';
import type { SkillSlotData } from './SkillSlot';

// Slide-in (right to left) when the game boots.
const ENTER_DURATION_SEC = 0.7;

// Cover-fit placement of a texture over the visible viewport (design coords).
type CoverPlacement = { x: number; y: number; width: number; height: number };

function coverViewport(tex: Texture, vp: DesignViewport): CoverPlacement {
  const scale = Math.max(vp.width / tex.width, vp.height / tex.height);
  const width = tex.width * scale;
  const height = tex.height * scale;
  return {
    x: vp.x + (vp.width - width) / 2,
    y: vp.y + (vp.height - height) / 2,
    width,
    height,
  };
}

export type DesktopScreenOptions = {
  ui: UiTextures;
  enemies: EnemyCardData[];
  // The player's own skills shown on the Skills tab.
  playerSkills: SkillSlotData[];
  fontFamily?: string;
  textResolution: number;
  // Visible window bounds in design coords (extends past the design rect in
  // fullwindow mode). The desktop background covers it fully - the main
  // screen has no letterbox bars, unlike the battle.
  viewport: () => DesignViewport;
  onFightSelected: (enemyIndex: number) => void;
};

// The main meta screen: workshop desktop background with the game window on
// top. The game-section art is the same canvas size as main-bg and is meant
// to overlay it 1:1 - both get the identical cover-fit transform. Battle is a
// separate scene - main.ts swaps visibility between the two.
export class DesktopScreen extends Container {
  private readonly bg: Sprite;
  private readonly viewport: () => DesignViewport;
  private readonly onResize = (): void => this.layout();
  private readonly window: GameWindow;
  // Base height the GameWindow children were built at; resize re-fits via
  // container scale instead of rebuilding.
  private readonly windowBaseHeight: number;
  private windowTargetX = 0;
  private entering = false;

  constructor(opts: DesktopScreenOptions) {
    super();
    this.viewport = opts.viewport;

    this.bg = new Sprite(opts.ui.mainBg);
    this.addChild(this.bg);

    // Build the window at the cover height of the CURRENT viewport so text
    // is baked at (close to) its final on-screen size.
    const initialPlace = coverViewport(opts.ui.gameFrame, this.viewport());
    this.windowBaseHeight = initialPlace.height;
    this.window = new GameWindow({
      frame: opts.ui.gameFrame,
      paper: opts.ui.paper,
      height: initialPlace.height,
      fontFamily: opts.fontFamily,
      textResolution: opts.textResolution,
      buildContent: (id, w, h) => {
        if (id === 'city') {
          return new CityTab({ width: w, height: h });
        }
        if (id === 'skills') {
          return new SkillsTab({
            width: w,
            height: h,
            skills: opts.playerSkills,
            labelBg: opts.ui.labelBg,
            fontFamily: opts.fontFamily,
            textResolution: opts.textResolution,
          });
        }
        if (id === 'fights') {
          return new FightsTab({
            width: w,
            height: h,
            enemies: opts.enemies,
            cardBody: opts.ui.enemyCardBody,
            labelBg: opts.ui.labelBg,
            selectBtn: opts.ui.selectBtn,
            fontFamily: opts.fontFamily,
            textResolution: opts.textResolution,
            onSelect: opts.onFightSelected,
          });
        }
        return null;
      },
    });
    this.addChild(this.window);

    this.layout();
    window.addEventListener('resize', this.onResize);
  }

  // Cover-fit BOTH layers over the actual visible area with the same
  // transform so the window art lands exactly on the desktop art (1:1).
  private layout(): void {
    const vp = this.viewport();

    const bgPlace = coverViewport(this.bg.texture, vp);
    this.bg.scale.set(bgPlace.width / this.bg.texture.width);
    this.bg.position.set(bgPlace.x, bgPlace.y);

    const winPlace = coverViewport(this.window.texture, vp);
    this.window.scale.set(winPlace.height / this.windowBaseHeight);
    this.windowTargetX = winPlace.x;
    this.window.y = winPlace.y;
    // Don't fight the slide-in tween for x; otherwise snap to target.
    if (!this.entering) this.window.x = winPlace.x;
  }

  override destroy(...args: Parameters<Container['destroy']>): void {
    window.removeEventListener('resize', this.onResize);
    super.destroy(...args);
  }

  // Switch the game window to the Fights tab (e.g. after an aborted battle).
  showFightsTab(): void {
    this.window.setTab('fights');
  }

  // Boot animation: the game window slides in from beyond the right edge.
  playEnter(): Promise<void> {
    const vp = this.viewport();
    this.window.x = vp.x + vp.width;
    this.entering = true;
    return new Promise((resolve) => {
      const tween = gsap.to(this.window, {
        x: this.windowTargetX,
        duration: ENTER_DURATION_SEC,
        ease: 'power3.out',
        onComplete: () => {
          this.entering = false;
          resolve();
        },
      });
      // The global gsap timeline is slowed by BATTLE_PACE_SCALE for battle
      // pacing - UI motion should run at real speed, so cancel it out here.
      tween.timeScale(BATTLE_PACE_SCALE);
    });
  }
}
